import { AgGridAngular } from "@ag-grid-community/angular";
import {
  ColDef,
  ColGroupDef,
  GridOptions,
  ICellRendererParams,
  IRowNode,
  ITooltipParams,
} from "@ag-grid-community/core";

import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { Currency, Marketplace, Strategy } from "@front/m19-api-client";
import { getFilteredLeafNodes, gridConfig, PARENT_ASIN_COL_NAME, ProductGridData } from "@front/m19-grid-config";
import { Metric, MetricRegistry } from "@front/m19-metrics";
import { AdStatsEx } from "@front/m19-models";
import {
  AccountSelectionService,
  AsinService,
  AuthService,
  MetricsSelectorPage,
  MetricsSelectorKey,
  SbStrategyService,
  SdStrategyService,
  SpStrategyService,
  StatsService,
  UserSelectionService,
} from "@front/m19-services";
import {
  ActionButtonsComponent,
  ChartConfig,
  IMetricEvoGridComponent,
  ModalOptions,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import {
  DateAggregation,
  emptyAdStatEx,
  emptyVendorAdStatEx,
  mergeSeveralDates,
  mergeVendorAdStatsExByAsin,
  MetricTitlePipe,
  sumAdStats,
} from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { activeStrategiesColumn, CurrencyColumn, ImageColumn } from "@m19-board/grid-config/grid-columns";
import { GridConfigService } from "@m19-board/grid-config/grid-config.service";
import {
  VENDOR_AGED90P_SELLABLE_COST,
  VENDOR_AGED90P_SELLABLE_UNITS,
  VENDOR_OPEN_PURCHASE_ORDER_UNITS,
  VENDOR_OUT_OF_STOCK_VIEWS,
  VENDOR_SELLABLE_COST,
  VENDOR_SELLABLE_UNITS,
  VENDOR_SOROOS,
  VENDOR_UNSELLABLE_COST,
  VENDOR_UNSELLABLE_UNITS,
} from "@m19-board/models/MetricsDef";
import { AsinLinkComponent } from "@m19-board/product-view/asin-link.component";
import { ProductThumbnailGridComponent } from "@m19-board/product-view/product-thumbnail-grid.component";
import { constantField, CsvExportService, metricField, simpleField } from "@m19-board/services/csv-export.service";
import { LoadingService } from "@m19-board/services/loading.service";
import {
  ChartData,
  ChartModalComponent,
  ChartModalInputData,
} from "@m19-board/shared/chart-modal/chart-modal.component";
import { Filter } from "@m19-board/shared/compact-filter/compact-filter.component";
import { AsinFilter, AsinFilterService } from "@m19-board/shared/filter/asin-filter.service";
import { StatsComponent, StatsComponentGridConfig, StatsComponentMetricConfig } from "@m19-board/stats/stats.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import { combineLatest, filter, map, Observable, shareReplay, switchMap, take, tap } from "rxjs";

@Component({
  selector: "app-vendor-inventory",
  imports: [StatsComponent],
  templateUrl: "./vendor-inventory.component.html",
  providers: [AsinFilterService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class VendorInventoryComponent implements OnInit {
  private readonly asinFilterService = inject(AsinFilterService);
  private readonly statsService = inject(StatsService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly accountSelection = inject(AccountSelectionService);
  private readonly authService = inject(AuthService);
  private readonly asinService = inject(AsinService);
  private readonly translocoService = inject(TranslocoService);
  private readonly modalService = inject(ModalService);
  private readonly gridConfigService = inject(GridConfigService);
  private readonly loadingService = inject(LoadingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly csvExportService = inject(CsvExportService);
  private readonly toasterService = inject(ToasterService);
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly sbStrategyService = inject(SbStrategyService);
  private readonly sdStrategyService = inject(SdStrategyService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly metricTitlePipe = inject(MetricTitlePipe);

  private readonly METRICS = [
    VENDOR_SELLABLE_COST,
    VENDOR_SELLABLE_UNITS,
    VENDOR_UNSELLABLE_UNITS,
    VENDOR_UNSELLABLE_COST,
    VENDOR_OPEN_PURCHASE_ORDER_UNITS,
    VENDOR_AGED90P_SELLABLE_UNITS,
    VENDOR_AGED90P_SELLABLE_COST,
    VENDOR_SOROOS,
    VENDOR_OUT_OF_STOCK_VIEWS,
  ];
  protected readonly localStorageKey: MetricsSelectorKey = {
    page: MetricsSelectorPage.vendorInventory,
  };
  selectedMetrics = signal<Metric<AdStatsEx>[]>([]);
  selectedDateRange = toSignal(this.userSelectionService.selectedDateRange$, { requireSync: true });

  grid = signal<AgGridAngular | undefined>(undefined);

  // TODO: mutualize common logic with seller-inventory
  filters = toSignal<Filter<AsinFilter>[]>(
    this.asinFilterService.getAsinFilters(false).pipe(
      filter((filter) => filter.length > 0),
      take(1),
    ),
  );
  filteredAsins = toSignal(this.asinFilterService.filteredAsins$, { requireSync: true });

  private dailyInventory$ = combineLatest([
    this.accountSelection.singleAccountMarketplaceSelection$,
    this.userSelectionService.dateRange$,
    this.userSelectionService.selectedCurrency$,
  ]).pipe(
    tap(() => this.loadingService.loadingOn()),
    switchMap(([am, dr, currency]) =>
      this.statsService.getDailyVendorInventory(
        am.accountId,
        am.marketplace,
        currency,
        dr[0],
        dr[1],
        !!am.useSourcingMetrics,
      ),
    ),
    tap(() => this.loadingService.loadingOff()),
    shareReplay(1),
  );
  dailyInventory = toSignal<AdStatsEx[]>(this.dailyInventory$);
  dailyInventoryByAsin = computed(() => mergeVendorAdStatsExByAsin(this.dailyInventory() ?? []));

  totalInventorySales = computed(() => {
    if (!this.dailyInventory()?.length) return emptyVendorAdStatEx();
    return this.dailyInventory()
      ?.filter((data) => AsinFilterService.asinMatchesFilter(data.asin ?? "", this.filteredAsins()))
      .reduce<AdStatsEx>((acc, curr) => mergeSeveralDates(acc, curr), emptyAdStatEx());
  });

  readonly chartConfig = computed<ChartConfig<AdStatsEx, "line">>(() => ({
    type: "line",
    aspectRatio: 3,
    aggFunctions: [sumAdStats],
    metrics: this.selectedMetrics(),
    dateAggregation: this.dateAggregation(),
    dateRange: this.selectedDateRange(),
    metricsOnSameScale: [
      [VENDOR_SELLABLE_COST, VENDOR_UNSELLABLE_COST],
      [VENDOR_SELLABLE_UNITS, VENDOR_UNSELLABLE_UNITS],
    ],
  }));
  readonly chartData = computed(() => {
    return (
      this.dailyInventory()?.filter((data) =>
        AsinFilterService.asinMatchesFilter(data.asin ?? "", this.filteredAsins()),
      ) ?? []
    );
  });

  currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });
  accountMarketplace = toSignal(this.accountSelection.singleAccountMarketplaceSelection$);
  locale = toSignal(this.authService.loggedUser$.pipe(map((u) => u.locale)), { initialValue: "" });
  catalog = toSignal(
    this.accountSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.asinService.getCatalog(am.accountId, am.marketplace)),
    ),
  );

  dateAggregation = signal<DateAggregation>(DateAggregation.daily);
  metricsConfig = computed<StatsComponentMetricConfig<AdStatsEx>>(() => {
    return {
      data: this.totalInventorySales(),
      metrics: this.METRICS,
    };
  });
  gridConfig = computed<StatsComponentGridConfig<AdStatsEx>>(() => {
    return {
      gridData: this.dailyInventoryByAsin(),
      columnDefs: this.getColumnDefs(this.currency(), this.locale(), this.accountMarketplace()?.marketplace),
      gridOptions: this.getGridOptions(),
    };
  });

  private getColumnDefs(currency?: Currency, locale?: string, marketplace?: Marketplace): (ColDef | ColGroupDef)[] {
    if (!currency || !locale || !marketplace) return [];

    return [
      {
        headerName: this.translocoService.translate("sales-advertising.product_info", {}, "en"),
        headerValueGetter: () => this.translocoService.translate("sales-advertising.product_info"),
        marryChildren: true, // prevent column group break
        pinned: "left",
        lockPinned: true,
        children: [
          {
            ...ImageColumn,
            headerName: this.translocoService.translate("catalog-page.image", {}, "en"),
            headerValueGetter: () => this.translocoService.translate("catalog-page.image"),
            cellRendererSelector: (params) => {
              if (!params.value) return undefined;
              else {
                return {
                  component: ProductThumbnailGridComponent,
                  params: {
                    asin: params.value,
                    smallImg: true,
                    marketplace: marketplace,
                    customSizeClass: "size-16",
                  },
                };
              }
            },
          },
          {
            headerName: this.translocoService.translate("common.product", {}, "en"),
            headerValueGetter: () => this.translocoService.translate("common.product"),
            pinned: "left",
            colId: "asin_col",
            field: "asin",
            floatingFilter: true,
            filter: "agTextColumnFilter",
            cellRendererSelector: (params) => {
              if (params.node.group) return undefined;
              return {
                component: AsinLinkComponent,
                params: {
                  asin: params.value,
                  marketplace: marketplace,
                },
              };
            },
          },
          {
            headerName: this.translocoService.translate("common.title", {}, "en"),
            headerValueGetter: (params) => this.translocoService.translate("common.title"),
            pinned: "left",
            maxWidth: 400,
            floatingFilter: true,
            filter: "agTextColumnFilter",
            valueGetter: (params) =>
              this.catalog()
                ?.getProductTitles()
                ?.get(params.node?.group ? params.node.key : params.data.asin) ?? "-",
            tooltipValueGetter: (p: ITooltipParams) => p.value,
            cellClass: "sensitive-data",
          },
          {
            field: "parentAsin",
            colId: PARENT_ASIN_COL_NAME,
            headerName: this.translocoService.translate("product360.parent_asin", {}, "en"),
            headerValueGetter: () => this.translocoService.translate("product360.parent_asin"),
            hide: true,
            pinned: "left",
            enableRowGroup: true,
            valueGetter: (params) => {
              return (
                this.catalog()?.getParentAsin(params.node?.group ? params.node.key : params.data.asin) ??
                params.data?.asin
              );
            },
            cellClass: "sensitive-data",
          },
          activeStrategiesColumn(this.translocoService, this.strategies, marketplace),
        ],
      },
      ...this.gridConfigService.getMetricsColDef(this.METRICS, true).map((def) => ({
        ...def,
        children: def.children.map((c: ColDef) => {
          const m = MetricRegistry.get(c.colId!)!;
          return {
            ...c,
            headerName: this.translocoService.translate(this.metricTitlePipe.transform(c.colId!), {}, "en"),
            headerValueGetter: () => this.metricTitlePipe.transform(c.colId!),
            cellRenderer: IMetricEvoGridComponent,
            cellRendererParams: (params: ICellRendererParams) => {
              return {
                ...c.cellRendererParams(params),
                currency: currency,
                locale: locale,
              };
            },
          };
        }),
      })),
      {
        cellRenderer: ActionButtonsComponent,
        pinned: "right",
        cellRendererParams: (params: any) => {
          const actionButtons = [
            {
              icon: "chartLine",
              tooltipValue: `Display ${params.node.isRowPinned() ? "total" : ""} ASIN chart`,
              color: "white",
              onClick: () => {
                this.openChartModal(params.node);
              },
            },
          ];

          return { actionButtons };
        },
      },
      {
        ...CurrencyColumn,
        valueGetter: () => this.currency(),
      },
    ];
  }

  strategies = new Map<string, Strategy[]>();

  ngOnInit(): void {
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        switchMap((am) =>
          combineLatest([
            this.spStrategyService.getStrategiesPerAsin(am.accountId, am.marketplace),
            this.sbStrategyService.getStrategiesPerAsin(am.accountId, am.marketplace),
            this.sdStrategyService.getStrategiesPerAsin(am.accountId, am.marketplace),
          ]),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([spStrategiesPerAsin, sbStrategiesPerAsin, sdStrategiesPerAsin]) => {
        for (const [asin, strategies] of [...spStrategiesPerAsin, ...sbStrategiesPerAsin, ...sdStrategiesPerAsin]) {
          if (this.strategies.has(asin)) {
            this.strategies.get(asin)!.push(...strategies);
          } else {
            this.strategies.set(asin, strategies);
          }
        }
      });
  }

  private openChartModal(node: IRowNode<ProductGridData>) {
    const modalOption: ModalOptions<ChartModalInputData<AdStatsEx>> = {
      data: {
        locale: this.locale(),
        currency: this.currency(),
        dataSetConfiguration: {
          aggregationFunction: [sumAdStats],
        },
        chartData$: this.getModalData(node),
        selectedMetrics: [VENDOR_SELLABLE_COST, VENDOR_SELLABLE_UNITS],
        metrics: this.METRICS,
        localStorageKey: this.localStorageKey,
      },
    };
    this.modalService.openModal<ChartModalInputData<AdStatsEx>, void>(ChartModalComponent, modalOption);
  }

  private getModalData(node: IRowNode<ProductGridData>): Observable<ChartData<AdStatsEx>> {
    const targetedNodes = getFilteredLeafNodes(node, this.grid()?.api);
    return this.dailyInventory$.pipe(
      map((dailyInventory) => {
        const asins = new Set(targetedNodes.map((n) => n.data!.asin));
        const data: AdStatsEx[] = [];
        for (const d of dailyInventory) {
          if (asins.has(d.asin)) {
            data.push(d);
          }
        }
        const totalData = data.reduce((acc, curr) => mergeSeveralDates(acc, curr), emptyAdStatEx());
        return { data, totalData };
      }),
    );
  }

  private getGridOptions(): GridOptions {
    return {
      ...gridConfig({ gridKey: "vendorInventoryGrid", withFooterRow: true }),
      autoGroupColumnDef: { pinned: "left" },
      isExternalFilterPresent: () => {
        return true;
      },
      onGridReady: (params) => {
        this.asinFilterService.filteredAsins$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((filters) => {
          params.api.setGridOption("doesExternalFilterPass", (node: IRowNode) =>
            AsinFilterService.asinMatchesFilter(node.data.asin, filters),
          );
        });
      },
    };
  }

  filterChanged(filters: Filter<AsinFilter>[]) {
    this.asinFilterService.selectFilters(filters);

    if (!this.grid()?.api.isDestroyed()) {
      this.grid()?.api.onFilterChanged();
      this.grid()?.api.redrawRows();
      this.grid()?.api.refreshCells({ force: true });
      this.grid()?.api.hideOverlay();
    }
  }

  metricsChanged(metrics: Metric<AdStatsEx>[]) {
    this.selectedMetrics.set(metrics);
  }

  dateAggregationChanged(dateAggregation: DateAggregation) {
    this.dateAggregation.set(dateAggregation);
  }

  downloadFile() {
    if (!this.dailyInventoryByAsin().length) {
      this.toasterService.error("No data to export", "Export error");
      return;
    }
    this.csvExportService.exportCsv(
      {
        prefix: "inventory",
        accountGroupName: this.accountMarketplace()?.accountGroupName,
        marketplace: this.accountMarketplace()?.marketplace,
      },
      this.dailyInventoryByAsin(),
      [
        simpleField("asin"),
        constantField("currency", this.currency()),
        metricField(VENDOR_SELLABLE_COST),
        metricField(VENDOR_SELLABLE_UNITS),
        metricField(VENDOR_UNSELLABLE_COST),
        metricField(VENDOR_UNSELLABLE_UNITS),
        metricField(VENDOR_OPEN_PURCHASE_ORDER_UNITS),
        metricField(VENDOR_AGED90P_SELLABLE_COST),
        metricField(VENDOR_AGED90P_SELLABLE_UNITS),
        metricField(VENDOR_SOROOS),
        metricField(VENDOR_OUT_OF_STOCK_VIEWS),
      ],
    );
  }
}
